import React from 'react'
import { Container, Row, Col, Button, ButtonGroup, Collapse } from 'reactstrap'
import ReactFlow, { Background, MarkerType, Handle, Position } from 'reactflow'
import 'reactflow/dist/style.css'
import { getQuote } from '../model/request/quotes'
import { getSchedule } from '../model/request/schedule'
import { Quote } from '../model/Quote'
import { Schedule } from '../model/Schedule'

/**
 * Only used for nodes that are annotation targets, so a labeled arrow can
 * drop into a dedicated top handle without disturbing the existing
 * left/right handles real dependency arrows already rely on.
 */
function AnnotationTargetNode({ data }) {
    return (
        <>
            <Handle type="target" position={Position.Left} id="left" />
            <Handle type="target" position={Position.Top} id="top" />
            {data.label}
            <Handle type="source" position={Position.Right} id="right" />
        </>
    )
}

const NODE_TYPES = { annotationTarget: AnnotationTargetNode }

function nodeStyle(schedule) {
    if (schedule.is_complete) {
        return { background: 'var(--bs-success)', color: 'var(--bs-white)', border: '1px solid var(--bs-success)' }
    }
    if (schedule.start_time) {
        return { background: 'var(--bs-warning)', color: 'var(--bs-dark)', border: '1px solid var(--bs-warning)' }
    }
    return { background: 'var(--bs-danger)', color: 'var(--bs-white)', border: '1px solid var(--bs-danger)' }
}

function buildFlowGraph(schedules, globalSchedules, annotationsByScheduleId = {}) {
    if (schedules.length === 0) return { nodes: [], edges: [] }

    // Map from global index → local index
    const globalToLocal = {}
    schedules.forEach((s, li) => {
        const gi = globalSchedules.indexOf(s)
        globalToLocal[gi] = li
    })

    // Build in-degree and adjacency for topo sort (local indices)
    const inDegree = new Array(schedules.length).fill(0)
    const adj = schedules.map(() => [])
    schedules.forEach((s, li) => {
        const gi = globalSchedules.indexOf(s)
        ;(globalSchedules[gi]?.dependency || []).forEach(depGi => {
            const depLi = globalToLocal[depGi]
            if (depLi !== undefined) {
                adj[depLi].push(li)
                inDegree[li]++
            }
        })
    })

    // Kahn's algorithm → assign levels
    const level = new Array(schedules.length).fill(0)
    const queue = []
    inDegree.forEach((deg, i) => { if (deg === 0) queue.push(i) })
    while (queue.length > 0) {
        const cur = queue.shift()
        adj[cur].forEach(next => {
            level[next] = Math.max(level[next], level[cur] + 1)
            inDegree[next]--
            if (inDegree[next] === 0) queue.push(next)
        })
    }

    // Assign x/y positions
    const posInLevel = {}
    const isAnnotated = schedules.map(s => (annotationsByScheduleId[s.id] || []).length > 0)

    const nodes = schedules.map((s, li) => {
        const l = level[li]
        posInLevel[l] = (posInLevel[l] || 0)
        const y = posInLevel[l] * 100
        posInLevel[l]++
        return {
            id: String(li),
            ...(isAnnotated[li] ? { type: 'annotationTarget' } : {}),
            position: { x: l * 240, y },
            data: {
                label: (
                    <div style={{ textAlign: 'center', fontSize: '11px' }}>
                        <div style={{ fontWeight: '600' }}>{s.short_description || '(unnamed)'}</div>
                        {s.equip && <div style={{ opacity: 0.85 }}>{s.equip}{s.process ? ' · ' + s.process : ''}</div>}
                        {s.is_complete && s.end_time && (
                            <div style={{ opacity: 0.75, marginTop: '3px' }}>
                                {'Done: ' + new Date(s.end_time).toLocaleString()}
                            </div>
                        )}
                        {!s.is_complete && s.start_time && (
                            <div style={{ opacity: 0.75, marginTop: '3px' }}>
                                {'Started: ' + new Date(s.start_time).toLocaleString()}
                            </div>
                        )}
                    </div>
                ),
            },
            style: { ...nodeStyle(s), borderRadius: '6px', padding: '6px 10px', minWidth: '140px' },
            sourcePosition: 'right',
            targetPosition: 'left',
        }
    })

    const edges = []
    schedules.forEach((s, li) => {
        const gi = globalSchedules.indexOf(s)
        ;(globalSchedules[gi]?.dependency || []).forEach(depGi => {
            const depLi = globalToLocal[depGi]
            if (depLi !== undefined) {
                edges.push({
                    id: `e${depLi}-${li}`,
                    source: String(depLi),
                    target: String(li),
                    ...(isAnnotated[depLi] ? { sourceHandle: 'right' } : {}),
                    ...(isAnnotated[li] ? { targetHandle: 'left' } : {}),
                    markerEnd: { type: MarkerType.ArrowClosed },
                })
            }
        })
    })

    // Annotation labels (e.g. "You are here") sit in one horizontal row
    // centered above the whole diagram, with an arrow from each label down
    // to wherever its actual target node sits - even a different column.
    const annotated = schedules
        .map((s, li) => ({ li, labels: annotationsByScheduleId[s.id] || [] }))
        .filter(a => a.labels.length > 0)

    if (annotated.length > 0) {
        const xs = nodes.map(n => n.position.x)
        const centerX = (Math.min(...xs) + Math.max(...xs)) / 2
        const topY = Math.min(...nodes.map(n => n.position.y)) - 120
        const spacing = 180
        const startX = centerX - ((annotated.length - 1) * spacing) / 2

        annotated.forEach((a, idx) => {
            const annotationId = 'annotation-' + a.li
            const labelX = startX + idx * spacing
            const target = nodes.find(n => n.id === String(a.li))
            const goRight = target.position.x > labelX
            nodes.push({
                id: annotationId,
                position: { x: labelX, y: topY },
                data: { label: a.labels.join(', ') },
                style: {
                    background: 'var(--bs-info)', color: 'var(--bs-white)',
                    borderRadius: '6px', padding: '4px 8px', fontSize: '11px',
                    fontWeight: '700', minWidth: '80px', textAlign: 'center',
                },
                sourcePosition: goRight ? 'right' : 'left',
                draggable: false,
                selectable: false,
            })
            edges.push({
                id: 'eAnnotation-' + a.li,
                source: annotationId,
                target: String(a.li),
                targetHandle: 'top',
                type: 'smoothstep',
                style: { stroke: 'var(--bs-info)', strokeWidth: 2 },
                markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--bs-info)' },
            })
        })
    }

    return { nodes, edges }
}

class JobScheduleFlowViewController extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            quote: null,
            schedules: [],
            openGroups: {},
            groupBy: 'signType',
        }
    }

    componentDidMount() {
        const { id, schedules: propSchedules, quote: propQuote } = this.props
        if (propSchedules && propQuote) return

        Promise.all([
            propQuote ? Promise.resolve(null) : getQuote(id),
            propSchedules ? Promise.resolve(null) : getSchedule(id),
        ]).then(([quoteResult, scheduleResult]) => {
            const newState = {}
            if (!propQuote) {
                const quote = Quote.from(quoteResult.data.quote)
                quote.signs = quote.signs.filter(s => s.quantity > 0)
                newState.quote = quote
            }
            if (!propSchedules) {
                newState.schedules = scheduleResult.data.schedules
                    .map(Schedule.from)
                    .sort((a, b) => a.order_index - b.order_index)
            }
            this.setState(newState)
        }).catch(err => {
            if (this.props.showError) this.props.showError(err)
        })
    }

    toggleGroup(key) {
        this.setState(prev => ({
            openGroups: { ...prev.openGroups, [key]: prev.openGroups[key] === false ? true : false },
        }))
    }

    buildGroups() {
        const quote = this.props.quote || this.state.quote
        const schedules = this.props.schedules || this.state.schedules
        const { groupBy } = this.state
        if (!quote) return []

        const signById = {}
        quote.signs.forEach(s => { signById[s.id] = s })

        let groups

        if (groupBy === 'givenId') {
            const sorted = [...quote.signs].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
            groups = sorted.map(sign => ({
                key: sign.id,
                label: sign.given_id,
                schedules: schedules.filter(s => (s.sign_ids || []).includes(sign.id)),
            }))
        } else {
            const typeOrder = []
            const typeMap = {}
            quote.signs.forEach(s => {
                const tid = s.type?.id
                if (tid && !typeMap[tid]) {
                    typeMap[tid] = s.type?.label || tid
                    typeOrder.push(tid)
                }
            })
            groups = typeOrder.map(tid => ({
                key: tid,
                label: typeMap[tid],
                schedules: schedules.filter(s =>
                    (s.sign_ids || []).some(sid => signById[sid]?.type?.id === tid)
                ),
            }))
        }

        const general = schedules.filter(s => !s.sign_ids || s.sign_ids.length === 0)
        if (general.length > 0) {
            groups.push({ key: '__general__', label: 'General', schedules: general })
        }

        return groups.filter(g => g.schedules.length > 0)
    }

    render() {
        const { breadCrumbs, id } = this.props
        const { openGroups, groupBy } = this.state
        const schedules = this.props.schedules || this.state.schedules
        const groups = this.buildGroups()

        const annotationsByScheduleId = {}
        ;(this.props.taskAnnotations || []).forEach(a => {
            (annotationsByScheduleId[a.task_id] ||= []).push(a.label)
        })

        return (
            <Container>
                {breadCrumbs && breadCrumbs(5, id)}
                <div style={{ marginBottom: '12px' }}>
                    <ButtonGroup size="sm">
                        <Button color="primary" outline={groupBy !== 'givenId'}
                            onClick={() => this.setState({ groupBy: 'givenId' })}>Sign Type</Button>
                        <Button color="primary" outline={groupBy !== 'signType'}
                            style={{
                                'marginLeft': '8px',
                            }}
                            onClick={() => this.setState({ groupBy: 'signType' })}>Process</Button>
                    </ButtonGroup>
                </div>
                {groups.map(group => {
                    const { nodes, edges } = buildFlowGraph(group.schedules, schedules, annotationsByScheduleId)
                    const levelCount = Math.max(...nodes.map(n => Math.floor(n.position.y / 100) + 1), 1)
                    const height = Math.max(
                        levelCount * 100 + 80 + (this.props.taskAnnotations?.length > 0 ? 140 : 0), 180)
                    return (
                        <div key={group.key} style={{ marginBottom: '16px' }}>
                            <Button
                                color="secondary"
                                outline
                                onClick={() => this.toggleGroup(group.key)}
                                style={{ marginBottom: '6px', width: '100%', textAlign: 'left' }}
                            >
                                {openGroups[group.key] !== false ? '▾' : '▸'} {group.label} ({group.schedules.length} task{group.schedules.length !== 1 ? 's' : ''})
                            </Button>
                            <Collapse isOpen={openGroups[group.key] !== false}>
                                <div style={{ height: height + 'px', border: '1px solid var(--bs-border-color)', borderRadius: 'var(--bs-border-radius)' }}>
                                    <ReactFlow
                                        nodes={nodes}
                                        edges={edges}
                                        nodeTypes={NODE_TYPES}
                                        fitView
                                        fitViewOptions={{ padding: 0.2 }}
                                        nodesDraggable={false}
                                        nodesConnectable={false}
                                        elementsSelectable={false}
                                    proOptions={{ hideAttribution: true }}
                                    >
                                        <Background />
                                    </ReactFlow>
                                </div>
                            </Collapse>
                        </div>
                    )
                })}
                {groups.length === 0 && (
                    <Row><Col><p className="text-muted">No scheduled tasks found for this job.</p></Col></Row>
                )}
            </Container>
        )
    }
}

export default JobScheduleFlowViewController
